/**
 * CameraCaptureModal — cross-platform "Take Photo" experience.
 *
 * Uses navigator.mediaDevices.getUserMedia() to open the device camera
 * (works on Surface Pro, iPad Safari, iPhone Safari, Android Chrome, and
 * desktop browsers with a webcam). Falls back to the file picker with
 * `capture="environment"` if getUserMedia is unavailable or denied.
 *
 * Replaces the previous Take Photo button which only set
 * `capture="environment"` on a file input — that attribute is silently
 * ignored on Windows/desktop, so the user saw a generic file dialog
 * instead of the camera. This modal gives consistent behavior everywhere.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { Camera, X, RefreshCw, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export default function CameraCaptureModal({ open, onClose, onCapture, facing = 'environment' }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [snapshot, setSnapshot] = useState(null); // dataURL of captured frame
  const [currentFacing, setCurrentFacing] = useState(facing);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  const start = useCallback(async (mode) => {
    setError(null);
    setSnapshot(null);
    stop();
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('not_supported');
      return;
    }
    try {
      // First try the requested facing mode; if it fails on a desktop
      // without rear cam, fall back to any video.
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: mode }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
      } catch (_) {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      console.warn('[CameraCapture] getUserMedia failed:', e);
      if (e?.name === 'NotAllowedError') setError('denied');
      else if (e?.name === 'NotFoundError') setError('no_camera');
      else setError('failed');
    }
  }, [stop]);

  useEffect(() => {
    if (open) start(currentFacing);
    return stop;
  }, [open, currentFacing, start, stop]);

  const flipCamera = () => {
    setCurrentFacing(prev => (prev === 'environment' ? 'user' : 'environment'));
  };

  const takeShot = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = canvasRef.current || document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setSnapshot(dataUrl);
  };

  const accept = async () => {
    if (!snapshot) return;
    setBusy(true);
    try {
      // Convert data URL → File so it goes through the same upload path
      // as a picked file (multipart upload + server save).
      const blob = await (await fetch(snapshot)).blob();
      const file = new File([blob], `camera_${Date.now()}.jpg`, { type: 'image/jpeg' });
      // Dispatch a synthetic file input change event with one file
      onCapture?.({ target: { files: [file] } });
      onClose?.();
    } catch (e) {
      console.error('[CameraCapture] accept failed:', e);
      toast.error('Could not save the captured photo. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const retake = () => setSnapshot(null);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[10010] bg-black flex flex-col" data-testid="camera-modal">
      {/* Top bar */}
      <div className="flex items-center justify-between p-3 text-white">
        <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/10" data-testid="camera-close">
          <X className="w-5 h-5" />
        </Button>
        <span className="text-sm font-medium">{snapshot ? 'Review' : 'Take Photo'}</span>
        {!snapshot && (
          <Button variant="ghost" size="icon" onClick={flipCamera} className="text-white hover:bg-white/10" data-testid="camera-flip">
            <RefreshCw className="w-5 h-5" />
          </Button>
        )}
        {snapshot && <div className="w-10" />}
      </div>
      {/* Stage */}
      <div className="flex-1 flex items-center justify-center overflow-hidden">
        {error ? (
          <div className="text-center text-white px-6">
            <Camera className="w-12 h-12 mx-auto mb-3 opacity-60" />
            {error === 'denied' && <p>Camera access denied. Enable it in your browser settings.</p>}
            {error === 'no_camera' && <p>No camera detected on this device.</p>}
            {error === 'not_supported' && <p>This browser does not support camera capture.</p>}
            {error === 'failed' && <p>Could not open the camera. Try again or use Upload instead.</p>}
            <Button onClick={onClose} className="mt-4 bg-white text-black hover:bg-gray-200">Close</Button>
          </div>
        ) : snapshot ? (
          <img src={snapshot} alt="Camera capture preview" className="max-w-full max-h-full object-contain" />
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="max-w-full max-h-full object-contain"
            data-testid="camera-video"
          />
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>
      {/* Bottom bar */}
      <div className="p-6 flex items-center justify-center gap-6 bg-black">
        {!snapshot && !error && (
          <button
            onClick={takeShot}
            disabled={busy}
            className="w-20 h-20 rounded-full bg-white border-4 border-white/40 hover:scale-95 active:scale-90 transition-transform shadow-lg disabled:opacity-50"
            data-testid="camera-shutter"
          >
            <div className="w-full h-full rounded-full bg-white" />
          </button>
        )}
        {snapshot && (
          <>
            <Button variant="outline" onClick={retake} className="bg-transparent text-white border-white hover:bg-white/10" data-testid="camera-retake">
              Retake
            </Button>
            <Button onClick={accept} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="camera-accept">
              <Check className="w-4 h-4 mr-2" />
              Use Photo
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
